/**
 * LinePicker — the floating dropdown that opens when the user clicks
 * a receptor anchor in LineRenderer (or the "start here" affordance
 * on an empty line).
 *
 * Two flavors:
 *   - "extend": the source is an existing node in the line. We list
 *     entities the source connects to in the dataset (move.from /
 *     move.to / move.counters / counter.result). Options that already
 *     exist in the current line become "loop to existing" — picking
 *     them only creates a back-edge, no duplicate node.
 *   - "start": the line is empty. We list every position in the
 *     dataset (and optionally moves) as a seed candidate. Filterable
 *     by a search box because the catalog is ~hundreds of entries.
 *
 * Selecting an option dispatches store actions to mutate the active
 * line, then closes the picker.
 *
 * Positioning is done by the parent (it knows the receptor's pixel
 * coordinates within the renderer container). This component just
 * renders the floating panel itself.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppStore } from '../state/store';
import type { Counter, Move, Position } from '../types/domain';
import type { Line, LineNode, LineNodeRef } from '../types/gameplan';
import { normalizeForSearch, searchMatcher } from './search-text';

export type PickerSource =
  | { mode: 'start' }
  | { mode: 'extend'; sourceNode: LineNode };

type Props = {
  source: PickerSource;
  gameplanId: string;
  line: Line;
  positions: Position[];
  moves: Move[];
  counters: Counter[];
  onClose: () => void;
};

type Option = {
  /** Unique key for React + dedupe. */
  key: string;
  ref: LineNodeRef;
  label: string;
  /** Short context line under the label — "from X → Y" etc. */
  subtitle?: string;
  kind: 'position' | 'move' | 'counter';
  /** If non-null, picking creates only an edge to this existing
   *  line-node id rather than adding a fresh node. */
  loopToNodeId: string | null;
};

export function LinePicker({
  source,
  gameplanId,
  line,
  positions,
  moves,
  counters,
  onClose,
}: Props) {
  const addLineNode = useAppStore((s) => s.addLineNode);
  const addLineEdge = useAppStore((s) => s.addLineEdge);

  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside-click. The receptor button itself sits in the
  // parent SVG, so the parent passes its own "ignore this click"
  // logic via stopPropagation on the open call — outside-click here
  // catches everything else.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) onClose();
    };
    // Defer registration so the same click that opened us doesn't
    // immediately close us via outside-click.
    const t = setTimeout(() => {
      document.addEventListener('mousedown', onDown);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  // Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // O(1) lookups.
  const positionsById = useMemo(
    () => new Map(positions.map((p) => [p.id, p])),
    [positions],
  );
  const movesById = useMemo(
    () => new Map(moves.map((m) => [m.id, m])),
    [moves],
  );
  const countersById = useMemo(
    () => new Map(counters.map((c) => [c.id, c])),
    [counters],
  );

  // Build the options list. Two paths: start vs extend.
  const options = useMemo<Option[]>(() => {
    if (source.mode === 'start') {
      // Empty-line seed picker — every position in the dataset, plus
      // every move (in case the user wants to drill a move in
      // isolation). Positions first since gameplans naturally start
      // from a positional context.
      const out: Option[] = [];
      for (const p of positions) {
        out.push({
          key: `p:${p.id}`,
          ref: { kind: 'position', id: p.id },
          label: p.name,
          subtitle: p.zone,
          kind: 'position',
          loopToNodeId: null,
        });
      }
      for (const m of moves) {
        const fromName = positionsById.get(m.from)?.name ?? m.from;
        const toName = positionsById.get(m.to)?.name ?? m.to;
        out.push({
          key: `m:${m.id}`,
          ref: { kind: 'move', id: m.id },
          label: m.name,
          subtitle: `${fromName} → ${toName}`,
          kind: 'move',
          loopToNodeId: null,
        });
      }
      return out;
    }

    // Extend mode — what connects FROM the source entity in the
    // dataset.
    const { sourceNode } = source;
    const out: Option[] = [];

    // Helper: look for an existing line-node that already represents
    // this ref. We dedupe by kind + id (counters dedupe by id only
    // since two distinct counters with the same id wouldn't happen).
    const existingNodeFor = (ref: LineNodeRef): string | null => {
      for (const n of line.nodes) {
        if (n.ref.kind !== ref.kind) continue;
        if (n.ref.id === ref.id) return n.id;
      }
      return null;
    };

    // Skip an option entirely if picking it would do nothing new —
    // i.e. the source is already connected to the matching existing
    // node. Otherwise the picker would offer a "loop" option that
    // creates a duplicate edge no-op.
    const alreadyConnected = (toNodeId: string): boolean =>
      line.edges.some(
        (e) => e.from === sourceNode.id && e.to === toNodeId,
      );

    const pushOption = (opt: Option) => {
      if (opt.loopToNodeId !== null && alreadyConnected(opt.loopToNodeId)) {
        return; // already wired; nothing to add
      }
      out.push(opt);
    };

    if (sourceNode.ref.kind === 'position') {
      // From a position: every move whose from === this position.
      const posId = sourceNode.ref.id;
      for (const m of moves) {
        if (m.from !== posId) continue;
        const toName = positionsById.get(m.to)?.name ?? m.to;
        const ref: LineNodeRef = { kind: 'move', id: m.id };
        pushOption({
          key: `m:${m.id}`,
          ref,
          label: m.name,
          subtitle: `→ ${toName}`,
          kind: 'move',
          loopToNodeId: existingNodeFor(ref),
        });
      }
    } else if (sourceNode.ref.kind === 'move') {
      // From a move: the to-position is the natural continuation.
      const m = movesById.get(sourceNode.ref.id);
      if (m) {
        const toPos = positionsById.get(m.to);
        if (toPos) {
          const ref: LineNodeRef = { kind: 'position', id: toPos.id };
          pushOption({
            key: `p:${toPos.id}`,
            ref,
            label: toPos.name,
            subtitle: 'continuation',
            kind: 'position',
            loopToNodeId: existingNodeFor(ref),
          });
        }
        // And each counter that intercepts this move.
        for (const cid of m.counters ?? []) {
          const c = countersById.get(cid);
          if (!c) continue;
          const ref: LineNodeRef = {
            kind: 'counter',
            id: c.id,
            moveId: m.id,
          };
          pushOption({
            key: `c:${c.id}`,
            ref,
            label: c.name,
            subtitle: 'counter — opponent intercepts',
            kind: 'counter',
            loopToNodeId: existingNodeFor(ref),
          });
        }
      }
    } else {
      // From a counter: the counter's result position.
      const c = countersById.get(sourceNode.ref.id);
      if (c) {
        const resultPos = positionsById.get(c.result);
        if (resultPos) {
          const ref: LineNodeRef = { kind: 'position', id: resultPos.id };
          pushOption({
            key: `p:${resultPos.id}`,
            ref,
            label: resultPos.name,
            subtitle: 'result of counter',
            kind: 'position',
            loopToNodeId: existingNodeFor(ref),
          });
        }
      }
    }
    return out;
  }, [source, line.nodes, line.edges, positions, moves, counters, positionsById, movesById, countersById]);

  // Apply search filter.
  const filtered = useMemo(() => {
    // Emptiness tested on the FOLDED query, not the raw one: a box
    // holding only punctuation has nothing to filter by, and searchMatcher
    // would correctly match everything anyway — this just skips the walk.
    if (!normalizeForSearch(query)) return options;
    const match = searchMatcher(query);
    return options.filter((o) => match(o.label) || match(o.subtitle));
  }, [options, query]);

  // Partition: "loop to existing" goes in its own section so it's
  // discoverable but doesn't clutter the main list.
  const newOptions = filtered.filter((o) => o.loopToNodeId === null);
  const loopOptions = filtered.filter((o) => o.loopToNodeId !== null);

  // When the just-added entity is a MOVE with no counters (so its only
  // continuation is its to-position), automatically also add that
  // position + the connecting edge. Saves a click for the common
  // "just keep chaining" case while still letting moves-with-counters
  // pop the picker so the user can choose.
  const maybeAutoChain = (opt: Option, sourceNodeId: string) => {
    if (opt.ref.kind !== 'move') return;
    const m = movesById.get(opt.ref.id);
    if (!m) return;
    if (m.counters && m.counters.length > 0) return;
    const toPos = positionsById.get(m.to);
    if (!toPos) return;
    // If the to-position is already a node in this line, just wire an
    // edge to it (loop-back style). Otherwise add a fresh node.
    const existing = line.nodes.find(
      (n) => n.ref.kind === 'position' && n.ref.id === toPos.id,
    );
    if (existing) {
      // Avoid creating a duplicate edge if for some reason it already
      // exists (the source move was just created so this is defensive).
      const already = line.edges.some(
        (e) => e.from === sourceNodeId && e.to === existing.id,
      );
      if (!already) {
        addLineEdge(gameplanId, line.id, sourceNodeId, existing.id);
      }
    } else {
      const newPosId = addLineNode(gameplanId, line.id, {
        kind: 'position',
        id: toPos.id,
      });
      addLineEdge(gameplanId, line.id, sourceNodeId, newPosId);
    }
  };

  const pick = (opt: Option) => {
    if (source.mode === 'start') {
      // Start mode: add a fresh node, no edge needed.
      const startNodeId = addLineNode(gameplanId, line.id, opt.ref);
      // Even starting with a move can auto-chain into the to-position
      // so the user gets a 2-node seed instead of just one.
      maybeAutoChain(opt, startNodeId);
      onClose();
      return;
    }
    const sourceNodeId = source.sourceNode.id;
    if (opt.loopToNodeId !== null) {
      // Loop-back: just create the edge. No auto-chain — the user
      // explicitly picked an existing node.
      addLineEdge(gameplanId, line.id, sourceNodeId, opt.loopToNodeId);
    } else {
      // Add new node + edge from source, then auto-chain when the
      // new node is a counter-less move.
      const newId = addLineNode(gameplanId, line.id, opt.ref);
      addLineEdge(gameplanId, line.id, sourceNodeId, newId);
      maybeAutoChain(opt, newId);
    }
    onClose();
  };

  const totalCount = newOptions.length + loopOptions.length;
  const showSearch = source.mode === 'start' || options.length > 8;

  return (
    <div
      ref={containerRef}
      // stopPropagation on mousedown so the outside-click handler in
      // the parent doesn't see it as a "click outside the picker."
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        width: 240,
        maxHeight: 380,
        display: 'flex',
        flexDirection: 'column',
        background: '#11141d',
        border: '1px solid rgba(155,231,255,0.30)',
        borderRadius: 8,
        boxShadow: '0 10px 24px rgba(0,0,0,0.45)',
        color: '#e6e8ef',
        overflow: 'hidden',
        zIndex: 50,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '8px 10px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: 10,
              letterSpacing: 0.7,
              textTransform: 'uppercase',
              opacity: 0.6,
            }}
          >
            {source.mode === 'start' ? 'start line' : 'add next'}
          </span>
          <span style={{ fontSize: 10, opacity: 0.4 }}>
            {totalCount} {totalCount === 1 ? 'option' : 'options'}
          </span>
        </div>
        {showSearch && (
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              source.mode === 'start'
                ? 'Search positions or moves…'
                : 'Filter…'
            }
            style={{
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 4,
              color: '#e6e8ef',
              fontSize: 12,
              padding: '4px 8px',
              outline: 'none',
            }}
          />
        )}
      </div>

      {/* Options list */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {totalCount === 0 ? (
          <div
            style={{
              padding: '20px 12px',
              fontSize: 11,
              opacity: 0.5,
              textAlign: 'center',
              lineHeight: 1.55,
            }}
          >
            {source.mode === 'start'
              ? 'No matches.'
              : "Nothing in the dataset connects from here. Counters can't extend further in this phase."}
          </div>
        ) : (
          <>
            {newOptions.length > 0 && (
              <OptionGroup
                title={source.mode === 'start' ? null : 'New node'}
                options={newOptions}
                onPick={pick}
              />
            )}
            {loopOptions.length > 0 && (
              <OptionGroup
                title="Loop to existing"
                options={loopOptions}
                onPick={pick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// Option group + row
// -----------------------------------------------------------------

function OptionGroup({
  title,
  options,
  onPick,
}: {
  title: string | null;
  options: Option[];
  onPick: (o: Option) => void;
}) {
  return (
    <div>
      {title && (
        <div
          style={{
            padding: '8px 10px 4px',
            fontSize: 9,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            opacity: 0.45,
          }}
        >
          {title}
        </div>
      )}
      {options.map((o) => (
        <OptionRow key={o.key} option={o} onPick={onPick} />
      ))}
    </div>
  );
}

function OptionRow({
  option,
  onPick,
}: {
  option: Option;
  onPick: (o: Option) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const kindColor = optionKindColor(option.kind);
  return (
    <button
      onClick={() => onPick(option)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%',
        textAlign: 'left',
        background: hovered ? 'rgba(155,231,255,0.10)' : 'transparent',
        border: 'none',
        color: '#e6e8ef',
        cursor: 'pointer',
        padding: '6px 10px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span
        style={{
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: kindColor,
          minWidth: 30,
          flexShrink: 0,
        }}
      >
        {option.loopToNodeId !== null ? '↺' : ''} {option.kind === 'counter' ? 'ctr' : option.kind.slice(0, 3)}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {option.label}
        </div>
        {option.subtitle && (
          <div
            style={{
              fontSize: 10,
              opacity: 0.55,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {option.subtitle}
          </div>
        )}
      </span>
    </button>
  );
}

function optionKindColor(kind: 'position' | 'move' | 'counter'): string {
  switch (kind) {
    case 'position':
      return '#9be7ff';
    case 'move':
      return '#ffcc88';
    case 'counter':
      return '#ffaab2';
  }
}
